import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import PageLayout, { PageHeader } from "@/components/PageLayout";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon, Clock, MapPin, ArrowLeft, CheckCircle, XCircle, 
  AlertCircle, PawPrint, Stethoscope, Dog, Loader2, CreditCard, Euro,
  ChevronLeft, ChevronRight, Plus
} from "lucide-react";

const statusConfig = {
  pending: { label: "In attesa", color: "text-amber-600", bg: "bg-amber-50", icon: Clock },
  confirmed: { label: "Confermato", color: "text-emerald-600", bg: "bg-emerald-50", icon: CheckCircle },
  cancelled: { label: "Annullato", color: "text-red-600", bg: "bg-red-50", icon: XCircle },
  completed: { label: "Completato", color: "text-blue-600", bg: "bg-blue-50", icon: CheckCircle }
};

const paymentStatusConfig = {
  unpaid: { label: "Da pagare", color: "text-orange-600", bg: "bg-orange-50" },
  pending: { label: "In corso", color: "text-amber-600", bg: "bg-amber-50" },
  paid: { label: "Pagato", color: "text-emerald-600", bg: "bg-emerald-50" },
  failed: { label: "Fallito", color: "text-red-600", bg: "bg-red-50" },
  expired: { label: "Scaduto", color: "text-slate-600", bg: "bg-slate-50" }
};

const SERVICE_PRICES = {
  visita: 50.00, pensione: 35.00, lezione: 40.00, consulenza: 30.00,
  toelettatura: 45.00, dog_sitting: 25.00, cat_sitting: 20.00, default: 30.00
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export default function Bookings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [payingBookingId, setPayingBookingId] = useState(null);
  const [pollingSessionId, setPollingSessionId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check for payment return
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (paymentStatus === "success" && sessionId) {
      setPollingSessionId(sessionId);
      toast.info("Verifica pagamento in corso...");
    } else if (paymentStatus === "cancelled") {
      toast.error("Pagamento annullato");
      navigate("/bookings", { replace: true });
    }
  }, [searchParams, navigate]);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error("Verifica pagamento scaduta. Controlla la tua email.");
      setPollingSessionId(null);
      navigate("/bookings", { replace: true });
      return;
    }
    
    try {
      const response = await authFetch(`${API}/payments/status/${sessionId}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === "paid") {
          toast.success("Pagamento completato!");
          setPollingSessionId(null);
          navigate("/bookings", { replace: true });
          fetchBookings();
          return;
        } else if (data.status === "expired") {
          toast.error("Sessione di pagamento scaduta.");
          setPollingSessionId(null);
          navigate("/bookings", { replace: true });
          return;
        }
        
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [navigate]);

  useEffect(() => {
    if (pollingSessionId) pollPaymentStatus(pollingSessionId);
  }, [pollingSessionId, pollPaymentStatus]);

  const fetchBookings = async () => {
    try {
      let url = `${API}/bookings`;
      if (filter !== "all") url += `?status=${filter}`;
      
      const response = await authFetch(url);
      if (response.ok) setBookings(await response.json());
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [filter]);

  const handlePayment = async (bookingId) => {
    setPayingBookingId(bookingId);
    
    try {
      const response = await authFetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: bookingId, origin_url: window.location.origin })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel pagamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setPayingBookingId(null);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!confirm("Annullare questa prenotazione?")) return;
    
    try {
      const response = await authFetch(`${API}/bookings/${bookingId}/status?status=cancelled`, { method: 'PUT' });
      
      if (response.ok) {
        setBookings(bookings.map(b => b.booking_id === bookingId ? { ...b, status: "cancelled" } : b));
        toast.success("Prenotazione annullata");
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const isUpcoming = (dateStr) => {
    const bookingDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate >= today;
  };

  const getServicePrice = (serviceType) => SERVICE_PRICES[serviceType] || SERVICE_PRICES.default;

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    return { daysInMonth, startingDay };
  };

  const getBookingsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.date === dateStr);
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const upcomingBookings = bookings.filter(b => isUpcoming(b.date) && b.status !== "cancelled");
  const pastBookings = bookings.filter(b => !isUpcoming(b.date) || b.status === "cancelled");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="animate-spin text-[#FF6B4A]" size={32} />
      </div>
    );
  }

  if (pollingSessionId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] px-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center mb-6 animate-pulse">
          <CreditCard className="text-white" size={36} />
        </div>
        <h2 className="text-xl font-bold text-[#1D1D1F] mb-2">Verifica pagamento...</h2>
        <p className="text-[#86868B]">Non chiudere questa pagina.</p>
      </div>
    );
  }

  // Desktop Layout
  if (!isMobile) {
    const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
    const calendarDays = [];
    
    for (let i = 0; i < startingDay; i++) {
      calendarDays.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push(i);
    }

    const selectedBookings = selectedDate ? getBookingsForDate(selectedDate) : upcomingBookings;

    return (
      <PageLayout data-testid="bookings-page">
        {/* Header */}
        <PageHeader 
          title="Prenotazioni"
          subtitle={`${bookings.length} prenotazioni totali`}
          icon={CalendarIcon}
          iconColor="#FF6B4A"
          action={
            <Button 
              onClick={() => navigate('/marketplace')}
              className="bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl px-6"
            >
              <Plus size={18} className="mr-2" />
              Nuova Prenotazione
            </Button>
          }
        />

          <div className="grid grid-cols-12 gap-6">
            {/* Calendar */}
            <div className="col-span-5">
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-black/5">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-[#1D1D1F]">
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <div className="flex gap-2">
                    <button onClick={prevMonth} className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center hover:bg-[#EBEBEB] transition-colors">
                      <ChevronLeft size={20} />
                    </button>
                    <button onClick={nextMonth} className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center hover:bg-[#EBEBEB] transition-colors">
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Days Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS.map(day => (
                    <div key={day} className="text-center text-sm font-medium text-[#86868B] py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, idx) => {
                    if (!day) return <div key={idx} className="aspect-square" />;
                    
                    const dayBookings = getBookingsForDate(day);
                    const hasBookings = dayBookings.length > 0;
                    const isSelected = selectedDate === day;
                    const isToday = new Date().getDate() === day && 
                                   new Date().getMonth() === currentDate.getMonth() &&
                                   new Date().getFullYear() === currentDate.getFullYear();
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedDate(isSelected ? null : day)}
                        className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all ${
                          isSelected ? 'bg-[#FF6B4A] text-white' :
                          isToday ? 'bg-[#1D1D1F] text-white' :
                          hasBookings ? 'bg-[#FF6B4A]/10 hover:bg-[#FF6B4A]/20' :
                          'hover:bg-[#F5F5F7]'
                        }`}
                      >
                        <span className={`text-sm font-medium ${isSelected || isToday ? 'text-white' : ''}`}>
                          {day}
                        </span>
                        {hasBookings && !isSelected && (
                          <div className="flex gap-0.5 mt-0.5">
                            {dayBookings.slice(0, 3).map((_, i) => (
                              <div key={i} className={`w-1.5 h-1.5 rounded-full ${isToday ? 'bg-white/70' : 'bg-[#FF6B4A]'}`} />
                            ))}
                          </div>
                        )}
                        {hasBookings && isSelected && (
                          <span className="text-xs text-white/80">{dayBookings.length}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-6 pt-4 border-t border-black/5">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#1D1D1F]" />
                    <span className="text-xs text-[#86868B]">Oggi</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#FF6B4A]" />
                    <span className="text-xs text-[#86868B]">Con appuntamenti</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bookings List */}
            <div className="col-span-7">
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-black/5">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-[#1D1D1F]">
                    {selectedDate ? `${selectedDate} ${MONTHS[currentDate.getMonth()]}` : 'Prossimi Appuntamenti'}
                  </h2>
                  
                  {/* Filters */}
                  <div className="flex gap-2">
                    {['all', 'pending', 'confirmed'].map(f => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          filter === f ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73] hover:bg-[#EBEBEB]'
                        }`}
                      >
                        {f === 'all' ? 'Tutte' : f === 'pending' ? 'In attesa' : 'Confermate'}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="mx-auto text-[#D1D1D6] mb-4" size={48} />
                    <h3 className="font-semibold text-[#1D1D1F] mb-2">
                      {selectedDate ? 'Nessun appuntamento' : 'Nessuna prenotazione'}
                    </h3>
                    <p className="text-[#86868B] mb-4">
                      {selectedDate ? 'Non hai appuntamenti in questa data' : 'Non hai ancora prenotazioni'}
                    </p>
                    <Button onClick={() => navigate('/marketplace')} className="bg-[#1D1D1F] rounded-xl">
                      Prenota un servizio
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedBookings.map((booking) => {
                      const status = statusConfig[booking.status] || statusConfig.pending;
                      const paymentStatus = paymentStatusConfig[booking.payment_status] || paymentStatusConfig.unpaid;
                      const StatusIcon = status.icon;
                      const needsPayment = booking.payment_status === "unpaid" && booking.status !== "cancelled";
                      const price = getServicePrice(booking.service_type);
                      
                      return (
                        <div 
                          key={booking.booking_id}
                          className="bg-[#F5F5F7] rounded-2xl p-5 hover:bg-[#EBEBEB] transition-all"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-4">
                              <div className={`w-14 h-14 rounded-2xl ${status.bg} flex items-center justify-center`}>
                                <Stethoscope className={status.color} size={28} />
                              </div>
                              <div>
                                <p className="font-bold text-[#1D1D1F] text-lg">{booking.professional_name}</p>
                                <p className="text-[#86868B] capitalize">{booking.service_type}</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className={`${status.bg} ${status.color} px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-1.5`}>
                                <StatusIcon size={14} />
                                {status.label}
                              </span>
                              <span className={`${paymentStatus.bg} ${paymentStatus.color} px-2 py-0.5 rounded text-xs font-medium`}>
                                {paymentStatus.label}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6 mb-4">
                            <div className="flex items-center gap-2 text-[#6E6E73]">
                              <CalendarIcon size={16} />
                              <span>{formatDate(booking.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[#6E6E73]">
                              <Clock size={16} />
                              <span>{booking.time_slot}</span>
                            </div>
                            {booking.pet_name && (
                              <div className="flex items-center gap-2 text-[#6E6E73]">
                                <PawPrint size={16} />
                                <span>{booking.pet_name}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between">
                            {needsPayment && (
                              <Button
                                onClick={() => handlePayment(booking.booking_id)}
                                disabled={payingBookingId === booking.booking_id}
                                className="bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl"
                              >
                                {payingBookingId === booking.booking_id ? (
                                  <Loader2 className="animate-spin mr-2" size={16} />
                                ) : (
                                  <CreditCard className="mr-2" size={16} />
                                )}
                                Paga €{price.toFixed(2)}
                              </Button>
                            )}
                            
                            {booking.payment_status === "paid" && (
                              <div className="flex items-center gap-2 text-emerald-600">
                                <CheckCircle size={18} />
                                <span className="font-medium">€{price.toFixed(2)} pagato</span>
                              </div>
                            )}
                            
                            {booking.status === "pending" && (
                              <button
                                onClick={() => handleCancelBooking(booking.booking_id)}
                                className="text-sm text-red-500 hover:text-red-600 font-medium ml-auto"
                              >
                                Annulla
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Past bookings section */}
                {!selectedDate && pastBookings.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-black/5">
                    <h3 className="text-sm font-semibold text-[#86868B] uppercase tracking-wider mb-4">Storico</h3>
                    <div className="space-y-2">
                      {pastBookings.slice(0, 5).map((booking) => {
                        const status = statusConfig[booking.status] || statusConfig.pending;
                        return (
                          <div key={booking.booking_id} className="flex items-center justify-between py-3 px-4 bg-[#F5F5F7] rounded-xl opacity-70">
                            <div>
                              <p className="font-medium text-[#1D1D1F]">{booking.professional_name}</p>
                              <p className="text-sm text-[#86868B]">{formatDate(booking.date)}</p>
                            </div>
                            <span className={`${status.bg} ${status.color} px-2 py-0.5 rounded text-xs font-medium`}>
                              {status.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      </PageLayout>
    );
  }

  // Mobile Layout with Calendar on top
  const { daysInMonth: mDays, startingDay: mStartDay } = getDaysInMonth(currentDate);
  const mobileCalendarDays = [];
  for (let i = 0; i < mStartDay; i++) mobileCalendarDays.push(null);
  for (let i = 1; i <= mDays; i++) mobileCalendarDays.push(i);
  
  const mobileSelectedBookings = selectedDate ? getBookingsForDate(selectedDate) : upcomingBookings;
  
  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-28" data-testid="bookings-page-mobile">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-black/5 sticky top-0 z-40">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Le mie prenotazioni
            </h1>
            <p className="text-sm text-[#86868B]">{bookings.length} prenotazioni</p>
          </div>
          <Button 
            size="sm"
            onClick={() => navigate('/marketplace')}
            className="bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl"
          >
            <Plus size={16} />
          </Button>
        </div>
      </div>
      
      <div className="px-4 py-4 space-y-4">
        {/* Mobile Mini Calendar */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
          {/* Month Header */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-[#1D1D1F]">
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="flex gap-1">
              <button onClick={prevMonth} className="w-8 h-8 rounded-lg bg-[#F5F5F7] flex items-center justify-center">
                <ChevronLeft size={16} />
              </button>
              <button onClick={nextMonth} className="w-8 h-8 rounded-lg bg-[#F5F5F7] flex items-center justify-center">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          
          {/* Days Header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-[#86868B] py-1">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {mobileCalendarDays.map((day, idx) => {
              if (!day) return <div key={idx} className="aspect-square" />;
              
              const dayBookings = getBookingsForDate(day);
              const hasBookings = dayBookings.length > 0;
              const isSelected = selectedDate === day;
              const isToday = new Date().getDate() === day && 
                             new Date().getMonth() === currentDate.getMonth() &&
                             new Date().getFullYear() === currentDate.getFullYear();
              
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDate(isSelected ? null : day)}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all text-sm ${
                    isSelected ? 'bg-[#FF6B4A] text-white' :
                    isToday ? 'bg-[#1D1D1F] text-white' :
                    hasBookings ? 'bg-[#FF6B4A]/10' :
                    ''
                  }`}
                >
                  <span className="font-medium">{day}</span>
                  {hasBookings && !isSelected && (
                    <div className={`w-1 h-1 rounded-full mt-0.5 ${isToday ? 'bg-white/70' : 'bg-[#FF6B4A]'}`} />
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Selected date label */}
          {selectedDate && (
            <div className="mt-3 pt-3 border-t border-black/5 flex items-center justify-between">
              <span className="text-sm font-medium text-[#1D1D1F]">
                {selectedDate} {MONTHS[currentDate.getMonth()]}
              </span>
              <button 
                onClick={() => setSelectedDate(null)}
                className="text-xs text-[#FF6B4A] font-medium"
              >
                Mostra tutte
              </button>
            </div>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {[{ value: "all", label: "Tutte" }, { value: "pending", label: "In attesa" }, { value: "confirmed", label: "Confermate" }].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                filter === f.value ? 'bg-[#1D1D1F] text-white' : 'bg-white text-[#6E6E73] border border-black/5'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        
        {/* Bookings List */}
        {mobileSelectedBookings.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-black/5">
            <CalendarIcon className="text-[#D1D1D6] mx-auto mb-4" size={48} />
            <h3 className="font-semibold text-[#1D1D1F] mb-2">
              {selectedDate ? 'Nessun appuntamento' : 'Nessuna prenotazione'}
            </h3>
            <p className="text-sm text-[#86868B] mb-4">
              {selectedDate ? 'Non hai appuntamenti in questa data' : 'Non hai ancora prenotazioni'}
            </p>
            <Button onClick={() => navigate('/marketplace')} className="bg-[#1D1D1F] rounded-xl">
              Esplora servizi
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {mobileSelectedBookings.map((booking) => {
              const status = statusConfig[booking.status];
              const StatusIcon = status.icon;
              const needsPayment = booking.payment_status === "unpaid" && booking.status !== "cancelled";
              const price = getServicePrice(booking.service_type);
              
              return (
                <div key={booking.booking_id} className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                  <div className={`h-1 ${booking.payment_status === 'paid' ? 'bg-emerald-500' : booking.status === 'pending' ? 'bg-amber-500' : 'bg-[#D1D1D6]'}`} />
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl ${status.bg} flex items-center justify-center`}>
                          <Stethoscope className={status.color} size={22} />
                        </div>
                        <div>
                          <p className="font-semibold text-[#1D1D1F]">{booking.professional_name}</p>
                          <p className="text-sm text-[#86868B] capitalize">{booking.service_type}</p>
                        </div>
                      </div>
                      <span className={`${status.bg} ${status.color} px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1`}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </div>
                    
                    <div className="bg-[#F5F5F7] rounded-xl p-3 mb-3 flex items-center gap-4 text-sm text-[#6E6E73]">
                      <span className="flex items-center gap-1.5"><CalendarIcon size={14} />{formatDate(booking.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock size={14} />{booking.time_slot}</span>
                    </div>
                    
                    {needsPayment && (
                      <Button
                        onClick={() => handlePayment(booking.booking_id)}
                        disabled={payingBookingId === booking.booking_id}
                        className="w-full bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl"
                      >
                        {payingBookingId === booking.booking_id ? <Loader2 className="animate-spin mr-2" size={16} /> : <CreditCard className="mr-2" size={16} />}
                        Paga €{price.toFixed(2)}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
